import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authUtil } from 'src/utils';

export type AudiobookVoiceSample = {
  id: string;
  status: 'queued' | 'generating' | 'ready' | 'error';
  creditsCharged: number;
  error?: string;
  createdAt?: string;
};

export type AudiobookVoice = {
  id: string;
  displayName: string;
  notes: string;
  languageCode: string;
  originLanguage?: {
    code: string;
    name: string;
    locale?: string | null;
    region?: string | null;
    accent?: string | null;
  } | null;
  sample?: AudiobookVoiceSample | null;
};

export type AudiobookVoiceSamples = {
  contentVersionId: string;
  openingSentence: string | null;
  maxSampleChars: number;
  sampleCredits: number;
  credits?: { balance?: number };
  configured: boolean;
  voices: AudiobookVoice[];
  preferredVoiceId?: string | null;
  rightsAttested: boolean;
  production?: { approvedVoiceId: string; approvedAt?: string } | null;
};

export type AudiobookDeliveries = {
  narrator: string;
  chapters: Array<{
    partId: string;
    number: number;
    title: string;
    status: 'ready' | 'pending';
    durationMs?: number;
  }>;
  exports: {
    status: 'waiting' | 'queued' | 'packaging' | 'ready' | 'error';
    error?: string;
    hasZip?: boolean;
    hasM4b?: boolean;
  };
};

const apiBase = () =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}`;

const authedFetch = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'br-token': await authUtil.getFreshIdToken(),
      ...(init.headers || {}),
    },
  });
  if (!response.ok) {
    const body: any = await response.json().catch(() => ({}));
    const err: any = new Error(body.msg || body.error || response.statusText);
    err.code = body.error;
    err.status = response.status;
    throw err;
  }
  return response.json();
};

export const audiobookVoiceSampleKeys = {
  all: (bookId?: string) => ['audiobookVoiceSamples', bookId] as const,
  deliveries: (bookId?: string) => ['audiobookDeliveries', bookId] as const,
};

export const useAudiobookVoiceSamples = (bookId?: string) => {
  const queryClient = useQueryClient();
  const base = `/books/${bookId}/audiobook`;
  const query = useQuery<AudiobookVoiceSamples>({
    queryKey: audiobookVoiceSampleKeys.all(bookId),
    queryFn: () => authedFetch<AudiobookVoiceSamples>(`${base}/voice-samples`),
enabled: Boolean(bookId), refetchOnWindowFocus: false, staleTime: 3000, retry: 1 });
  const deliveryQuery = useQuery<AudiobookDeliveries>({
    queryKey: audiobookVoiceSampleKeys.deliveries(bookId),
    queryFn: () => authedFetch<AudiobookDeliveries>(`${base}/deliveries`),
enabled: Boolean(bookId) && Boolean(query.data?.production),
    refetchOnWindowFocus: false,
    staleTime: 3000,
    retry: 1,
  });
  const samplesInProgress = (query.data?.voices || []).some(voice =>
    ['queued', 'generating'].includes(voice.sample?.status || '')
  );
  useEffect(() => {
    if (!samplesInProgress) return undefined;
    const timer = setInterval(() => query.refetch(), 3000);
    return () => clearInterval(timer);
  }, [samplesInProgress, query]);
  useEffect(() => {
    const pending = ['waiting', 'queued', 'packaging'].includes(
      deliveryQuery.data?.exports?.status || ''
    );
    if (!pending) return undefined;
    const timer = setInterval(() => deliveryQuery.refetch(), 5000);
    return () => clearInterval(timer);
  }, [deliveryQuery]);

  const render = useMutation({
    mutationFn: ({ voiceId, sampleText }: { voiceId: string; sampleText: string }) =>
      authedFetch(`${base}/voice-samples`, {
        method: 'POST',
        body: JSON.stringify({ voiceId, sampleText }),
      }),

    onSuccess: () => queryClient.invalidateQueries({ queryKey: audiobookVoiceSampleKeys.all(bookId) })
  });
  const choose = useMutation({
    mutationFn: (voiceId: string) =>
      authedFetch(`${base}/voice-selection`, {
        method: 'PUT',
        body: JSON.stringify({ voiceId, rightsAttested: true }),
      }),

    onSuccess: () => queryClient.invalidateQueries({ queryKey: audiobookVoiceSampleKeys.all(bookId) })
  });
  const getStreamUrl = (sampleId: string) =>
    authedFetch<{ url: string }>(`${base}/voice-samples/${sampleId}/stream-url`);
  const getDeliveryDownload = (path: string) => authedFetch<{ url: string }>(`${base}/deliveries/${path}`);

  return {
    data: query.data,
    loading: query.isInitialLoading,
    error: query.error as any,
    render,
    choose,
    getStreamUrl,
    deliveries: deliveryQuery.data,
    deliveryError: deliveryQuery.error as any,
    getDeliveryDownload,
  };
};

export default useAudiobookVoiceSamples;
